import { Button, DatePicker, Input, List, Popover, Select, Space } from 'antd';
import { SelectProps } from 'antd/lib/select';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import VSpace from '../common/VSpace';
import { DateRangeOption, DateRanges, dateRanges, formatDateRange, getRangeName } from './datacenterModel';

const { RangePicker } = DatePicker;

type DateRangeFilter = {
  startDate: Dayjs;
  endDate: Dayjs;
  dateRange: DateRanges;
  [key: string]: any;
};

type Props<T extends DateRangeFilter> = {
  dateRangeTitle?: string;
  filters: T;
  onFiltersChange: (filters: T) => void;
  isDataLoading: boolean;
  defaultFilters: T;
  onSaveFilters: () => void;
  selectOptions?: {
    [key: string]: SelectProps<any>;
  };
  isSummaryMode?: boolean;
};

type PopoverContentProps<T extends DateRangeFilter> = {
  filters: T;
  onFiltersChange: (filters: T) => void;
  dateRangeProps?: any;
};

const ReportFilters = <T extends DateRangeFilter>({
  dateRangeTitle,
  filters,
  onFiltersChange,
  isDataLoading,
  defaultFilters,
  onSaveFilters,
  selectOptions,
  isSummaryMode = false,
}: Props<T>) => {
  const dateRangeName = getRangeName(filters.dateRange || 'custom');

  return (
    <Space wrap>
      { !isSummaryMode && <Popover
          placement='bottom'
          arrow={false}
          content={<PopoverContent filters={filters} onFiltersChange={onFiltersChange} />}
          trigger='click'
        >
          {dateRangeTitle && <b> {dateRangeTitle} </b>}
          <Input
            readOnly
            value={filters.dateRange === 'custom' ? formatDateRange(filters.startDate, filters.endDate) : `${dateRangeName} (${formatDateRange(filters.startDate, filters.endDate)})`}
            style={{ width: filters.dateRange === 'lastYear' ? 300 : 200 }}
          />
        </Popover>
      }
      {selectOptions &&
        Object.keys(selectOptions).map((key) => (
          <Select
            key={key}
            value={filters[key]}
            onChange={(value) => onFiltersChange({ ...filters, [key]: value })}
            {...selectOptions[key]}
          />
        ))
      }
      {!isSummaryMode && (
        <>
          <Button type="primary" onClick={onSaveFilters} disabled={isDataLoading}>
            Save Filters
          </Button>
          <Button onClick={() => onFiltersChange(defaultFilters || filters)}>
            Clear Filters
          </Button>
        </>
      )}
    </Space>
  );
};

const PopoverContent = <T extends DateRangeFilter>({ filters, onFiltersChange, dateRangeProps }: PopoverContentProps<T>) => {
  const {
    handleDateChange,
    handleRangeSelectChange,
    isCustomRangeSelected,
    selectedRange,
    setIsCustomRangeSelected
  } = useDateRange(filters, onFiltersChange, dateRanges);

  return (
    <>
      {!isCustomRangeSelected && (
        <List
          dataSource={dateRanges}
          renderItem={item => (
            <List.Item
              key={item.value}
              style={{ cursor: 'pointer', padding: '8px 16px', backgroundColor: selectedRange === item.value ? '#f0f0f0' : 'white' }}
              onClick={() => handleRangeSelectChange(item.value)}
            >
              {item.label}
            </List.Item>
          )}
        />
      )}
      {isCustomRangeSelected && (
        <VSpace>
          <RangePicker
            value={[filters.startDate, filters.endDate]}
            onChange={handleDateChange}
            allowClear={false}
            style={{ width: 300 }}
            {...dateRangeProps}
          />
          <Space>
            <Button onClick={() => setIsCustomRangeSelected(false)}>Cancel</Button>
          </Space>
        </VSpace>
      )}
    </>
  );
};

export default ReportFilters;

const useDateRange = <T extends DateRangeFilter>(
  filters: T,
  onFiltersChange: (filters: T) => void,
  customDateRanges: DateRangeOption[]
) => {
  const [isCustomRangeSelected, setIsCustomRangeSelected] = useState<boolean>(false);
  const [selectedRange, setSelectedRange] = useState<string | undefined>(() => {
    const matchingRange = customDateRanges.find(range =>
      range.range && range.range[0].isSame(filters.startDate, 'day') && range.range[1].isSame(filters.endDate, 'day')
      || filters.dateRange === range.value
    );
    return matchingRange ? matchingRange.value : 'custom';
  });

  const handleDateChange = (dates: [Dayjs, Dayjs] | null) => {
    const newStartDate = dates?.[0] || filters.startDate;
    const newEndDate = dates?.[1] || filters.endDate;
    onFiltersChange({
      ...filters,
      startDate: newStartDate,
      endDate: newEndDate,
    });
    const matchingRange = customDateRanges.find(range =>
      range.range && range.range[0].isSame(newStartDate, 'day') && range.range[1].isSame(newEndDate, 'day')
    );
    setSelectedRange(matchingRange ? matchingRange.value : 'custom');
  };

  const handleRangeSelectChange = (value: DateRanges) => {
    if (value === 'custom') {
      setSelectedRange(value);
      setIsCustomRangeSelected(true);
      onFiltersChange({
        ...filters,
        dateRange: value,
        startDate: dayjs().subtract(6, 'month'),
        endDate: dayjs(),
      })
    } else {
      const selectedRange = customDateRanges.find((range) => range.value === value)?.range;
      if (selectedRange) {
        setSelectedRange(value);
        setIsCustomRangeSelected(false);
        onFiltersChange({
          ...filters,
          dateRange: value,
          startDate: selectedRange[0],
          endDate: selectedRange[1],
        });
      }
    }
  };

  return {
    isCustomRangeSelected,
    selectedRange,
    handleDateChange,
    handleRangeSelectChange,
    setIsCustomRangeSelected,
  };
};